/**
 * Zweck: Beschreibt die TypeScript-Bausteine für den Frontend-Bereich frontend/src/app/app.component.ts.
 */
import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {AppEventService} from './services/app.event.service';
import {NavigationCancel, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {AppPageTitleService} from './services/app.page-title.service';
import {AppPageloaderService} from './services/app.pageloader.service';
import {Subject} from 'rxjs';
import {MatomoTracker} from './modules/matamo/service/matomo-tracker.service';
import {AppConfigService} from './services/app.config.service';
import {AppUpdateService} from './services/app.update.service';
import {AppPrivacyService} from './services/app.privacy.service';
import {AppUserService} from './services/app.user.service';
import {User} from './models/user';
import * as Sentry from '@sentry/angular';
import {EventHint} from '@sentry/angular';
import {SwPush} from '@angular/service-worker';
import {Logger} from './services/app.logger.service';
import {AppAsyncTimeout} from './helpers/app.asyncTimeout';
import {takeUntil} from 'rxjs/operators';

declare let window: any;

/**
 * Zweck: Beschreibt class AppComponent und ihren Daten- oder Funktionsvertrag.
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'frontend';

  user: User;

  sentryInitDone = false;

  _onDestroy = new Subject<void>();

  @HostListener('window:resize', ['$event'])
/**
 * Beschreibt Methode calcAppSize und ihren Rückgabevertrag.
 */
  calcAppSize() {
    const doc = document.documentElement;

    doc.style.setProperty('--app-height', `calc(${window.innerHeight}px - env(safe-area-inset-top))`);
    doc.style.setProperty('--app-width', `${window.innerWidth}px`);
  }

/**
 * Beschreibt Methode constructor und ihren Rückgabevertrag.
 */
  constructor(private esvc: AppEventService, private router: Router, private tracker: MatomoTracker, private psvc: AppPrivacyService,
              private cfg: AppConfigService, private tsvc: AppPageTitleService, private loader: AppPageloaderService,
              private swPush: SwPush, private updsvc: AppUpdateService, private usvc: AppUserService) {
  }

/**
 * Beschreibt Methode initSentryUser und ihren Rückgabevertrag.
 */
  initSentryUser() {
    if (this.psvc.settings.sentry_userid && this.user) {
      Sentry.setUser({
        id: this.user.id,
        email: this.user.email,
        username: this.user.benutzerandruckname
      });
    } else {
      Sentry.setUser(null);
    }
  }

/**
 * Beschreibt Methode initSentry und ihren Rückgabevertrag.
 */
  initSentry() {
    if ((this.psvc.settings.sentry || this.psvc.settings.sentry_performance) && !this.cfg.isDebug()  && !this.sentryInitDone) {
      this.sentryInitDone = true;
      Logger.info('AppComponent', 'initSentry', 'init!');
      Sentry.init({
        environment: window.location.host,
        dsn: 'https://160b503316d249569982ec3185d958ca@o982183.ingest.sentry.io/5937043',
        tunnel: '/api/sntunnel',
        integrations: [
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true
          }),
          Sentry.browserTracingIntegration(),
        ],
        release: 'jgmportal@' + this.cfg.getVersion(),
        ignoreErrors: [
          'Non-Error exception captured',
          'UnknownError'
        ],

        // @ts-ignore
        beforeSend: function (event: Event, hint?: EventHint): PromiseLike<Event | null> | Event | null {
          if (window.SENTRY_DEBUG && window.SENTRY_DEBUG == true) {
            return null;
          } else {
            return event;
          }
        },

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: this.psvc.settings.sentry_performance ? 0.2 : 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
      });

      this.initSentryUser();
    }

  }

/**
 * Beschreibt Methode subscribeToNotifications und ihren Rückgabevertrag.
 */
  subscribeToNotifications() {
    if (this.swPush.isEnabled) {
      this.swPush.requestSubscription({
        serverPublicKey: this.cfg.domainconfig.VAPID_PUBLIC_KEY
      })
        .then(sub => this.usvc.addDevice(sub).subscribe())
        .catch(err => console.error('Could not subscribe to notifications', err));

    }
  }


/**
 * Beschreibt Methode initMatomo und ihren Rückgabevertrag.
 */
  initMatomo() {
    if (this.cfg.matomo_enabled) {
      if (this.psvc.settings.matomo_userid || this.psvc.settings.matomo_pageview) {
        this.tracker.enableCrossDomainLinking();
        this.tracker.enableHeartBeatTimer(5);
      }
    }
  }

/**
 * Beschreibt Methode trackRouterMatomo und ihren Rückgabevertrag.
 */
  trackRouterMatomo() {
    if (this.cfg.matomo_enabled) {
      if (this.psvc.settings.matomo_pageview) {
        AppAsyncTimeout.setTimeout(() => {
          this.tracker.setCustomUrl(this.router.url);
          this.tracker.setReferrerUrl(this.router.url);
          this.tracker.trackPageView();
          this.tracker.trackAllContentImpressions();
        }, 100);
      }
    }
  }

/**
 * Beschreibt Methode trackImpressionsMatomo und ihren Rückgabevertrag.
 */
  trackImpressionsMatomo() {
    if (this.cfg.matomo_enabled) {
      if (this.psvc.settings.matomo_pageview) {
        this.tracker.trackAllContentImpressions();
      }
    }
  }

/**
 * Beschreibt Methode initMatomoUser und ihren Rückgabevertrag.
 */
  initMatomoUser(user: User) {
    if (this.cfg.matomo_enabled) {
      if (this.psvc.settings.matomo_userid && user) {
        this.tracker.setUserId(user.id);
      }
    }
  }

/**
 * Beschreibt Methode ngOnInit und ihren Rückgabevertrag.
 */
  ngOnInit(): void {
    console.debug('To enable/disable Debugging: ?_debugLogging=1/0');
    this.calcAppSize();
    this.psvc.hasSettings$.pipe(takeUntil(this._onDestroy)).subscribe(state => {
      this.initMatomo();
      this.initMatomoUser(this.user);
      this.trackImpressionsMatomo();
      this.trackRouterMatomo();
      this.initSentry();
    });
    this.usvc.user$.pipe(takeUntil(this._onDestroy)).subscribe(u => {
      this.user = u;
      this.initMatomoUser(u);
      this.initSentryUser();
    });
    this.updsvc.checkForUpdates();


    this.usvc.isLoggedin$.pipe(takeUntil(this._onDestroy)).subscribe(isLoggedin => {
      if (isLoggedin) {
        this.subscribeToNotifications();
      }
    });

    this.router.events.pipe(takeUntil(this._onDestroy)).subscribe(e => {
      if (e instanceof NavigationStart) {
        this.loader.stopAll();
        this.loader.start('Navigation');

      } else if (e instanceof NavigationEnd) {
        this.esvc.addGuiEvent('NavigationEnd');
        this.loader.stop('Navigation');

        this.trackRouterMatomo();

      } else if (e instanceof NavigationCancel) {
        this.loader.stop('Navigation');
      }
    });

    this.loader.loading$.pipe(takeUntil(this._onDestroy)).subscribe(s => {
      if (!s) {
        this.trackImpressionsMatomo();
      }
    });
  }

/**
 * Beschreibt Methode ngOnDestroy und ihren Rückgabevertrag.
 */
  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

/**
 * Beschreibt Methode bodyClick und ihren Rückgabevertrag.
 */
  bodyClick() {
    this.esvc.addGuiEvent('app_body_click');

    this.trackImpressionsMatomo();
  }
}
